import { KeyboardEvent, useEffect, useRef } from 'react';

import type { OpsSchedule } from '@/services/opsRota/types';
import styled from '@/styles';

import { useDismiss } from '../hooks/useDismiss';
import { blockRangeSpaced } from '../messages';
import { C } from '../Operations.styles';

import { KebabIcon } from './icons';

// UI spec §8.3.
const Kebab = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '24px',
  height: '24px',
  padding: 0,
  border: 'none',
  borderRadius: '6px',
  background: 'none',
  color: C.ink,
  cursor: 'pointer',
  '& svg': { width: '24px', height: '24px' },
  // Grey on hover only, never while the menu is open: the prototype's open
  // menu lays a click-catcher over the page, so the kebab loses its hover.
  '&:hover:not([aria-expanded="true"])': { background: C.hoverGrey },
  '&:focus-visible': { outline: `2px solid ${C.darkGreen}` },
});

const Menu = styled('div', {
  position: 'absolute',
  top: '40px',
  right: '8px',
  // Above the next block card, which it may overlap.
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  width: '110px',
  padding: '8px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.softBorder}, 0 2px 4px 0 rgba(0,0,0,0.08)`,
});

// Groups the kebab and its menu for useDismiss without adding a box: the
// menu still positions against the block card. `static` undoes the app's
// global `* { position: relative }`.
const Anchor = styled('span', { display: 'contents', position: 'static' });

const Item = styled('button', {
  display: 'flex',
  alignItems: 'center',
  height: '36px',
  padding: '0 16px',
  border: 'none',
  borderRadius: '12px',
  background: 'none',
  color: C.ink,
  font: 'inherit',
  fontSize: '14px',
  fontWeight: 400,
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover, &:focus-visible': { background: C.hoverGrey, outline: 'none' },
  // Keyboard focus is a visible ring, not only the hover grey.
  '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${C.darkGreen}` },
});

type Props = {
  schedule: OpsSchedule;
  /** Not editable (`schedule.editable` false: dated before today and ended): only Repeat. */
  readOnly: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: (s: OpsSchedule) => void;
  onRepeat: (s: OpsSchedule, trigger: HTMLElement | null) => void;
  onDelete: (s: OpsSchedule, trigger: HTMLElement | null) => void;
};

// The block's kebab and its menu: Edit, Repeat, Delete (in that order). The
// menu hangs off the block card (position: relative), not the button.
const BlockMenu = ({
  schedule,
  readOnly,
  open,
  onToggle,
  onClose,
  onEdit,
  onRepeat,
  onDelete,
}: Props) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = `opsScheduleMenu-${schedule.id}`;

  // The first item takes focus when the menu opens.
  useEffect(() => {
    if (open)
      menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open]);

  // A press outside the kebab and menu closes it; Esc closes it and hands
  // focus back to the kebab.
  useDismiss(anchorRef, open, (reason) => {
    onClose();
    if (reason === 'escape') buttonRef.current?.focus();
  });

  const pick =
    (action: (s: OpsSchedule, trigger: HTMLElement | null) => void) => () => {
      onClose();
      action(schedule, buttonRef.current);
    };

  const onMenuKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
    );
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      const step = ev.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (ev.key === 'Tab') {
      onClose();
    }
  };

  return (
    <Anchor ref={anchorRef}>
      <Kebab
        ref={buttonRef}
        type="button"
        aria-label={`More actions for ${blockRangeSpaced(schedule)}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={`opsScheduleMenuButton-${schedule.id}`}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle();
        }}
      >
        <KebabIcon />
      </Kebab>
      {open && (
        <Menu
          ref={menuRef}
          id={menuId}
          role="menu"
          data-testid="opsScheduleMenu"
          onKeyDown={onMenuKey}
        >
          {!readOnly && (
            <Item
              type="button"
              role="menuitem"
              data-testid="opsScheduleMenuEdit"
              onClick={pick(onEdit)}
            >
              Edit
            </Item>
          )}
          <Item
            type="button"
            role="menuitem"
            data-testid="opsScheduleMenuRepeat"
            onClick={pick(onRepeat)}
          >
            Repeat
          </Item>
          {!readOnly && (
            <Item
              type="button"
              role="menuitem"
              data-testid="opsScheduleMenuDelete"
              onClick={pick(onDelete)}
            >
              Delete
            </Item>
          )}
        </Menu>
      )}
    </Anchor>
  );
};

export default BlockMenu;
